import { Button } from '@storeblocks/button';
import { type FC, useState } from 'react';
import styled from 'styled-components';

import { QueryState } from '@/api/components/QueryState';
import {
  formatDateArgument,
  useFetchPortfolioPerformanceQuery,
} from '@/api/performanceData/performanceDataApi';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { isNotReady } from '@/api/utils/isNotReady';
import { UnitChips } from '@/components/Charts/components/UnitChips';
import { DateFilterType } from '@/components/DateFilter/types/DateFilterType';
import { getDateFilter } from '@/components/DateFilter/utils/getDateFilter';
import { FixedHeight } from '@/components/FixedHeight';
import { InternalLink } from '@/components/InternalLink';
import { PortfolioPerformanceGraph } from '@/components/PortfolioPerformanceGraph/PortfolioPerformanceGraph';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { Unit } from '@/types/Unit';

import { DateFilterChips } from './DateFilterChips';
import { DateRangeSelector } from './DateRangeSelector';
import { PortfolioReturnText } from './PortfolioReturnText';

interface Props {
  portfolioGroup: string;
  showMoreLink?: boolean;
}

const defaultDateFilterType = DateFilterType.ThisYear;

export const PortfolioPerformanceWidget: FC<Props> = ({
  portfolioGroup,
  showMoreLink,
}) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();
  const organizationCmId = useOrganizationCmId();

  const [selectedUnit, setSelectedUnit] = useState(Unit.Percent);
  const [selectedPeriod, setSelectedPeriod] = useState<DateFilterType>(
    defaultDateFilterType,
  );

  const [startDate, setStartDate] = useState<Date>(
    getDateFilter(defaultDateFilterType).from,
  );
  const [endDate, setEndDate] = useState<Date>(
    getDateFilter(defaultDateFilterType).to,
  );

  const portfolioPerformanceQuery = useFetchPortfolioPerformanceQuery({
    portfolioGroup,
    from: formatDateArgument(startDate),
    to: formatDateArgument(endDate),
  });

  const handlePeriodChange = (
    period: DateFilterType,
    from: Date | null,
    to: Date | null,
  ): void => {
    const previousPeriod = selectedPeriod;

    if (period === DateFilterType.Custom) {
      setStartDate(from ?? getDateFilter(previousPeriod).from);
      setEndDate(to ?? getDateFilter(previousPeriod).to);
    } else {
      setStartDate(getDateFilter(period).from);
      setEndDate(getDateFilter(period).to);
    }

    setSelectedPeriod(period);
  };

  return (
    <WithGap data-test={`portfolio-widget-performance-${portfolioGroup}`}>
      <WithGap gap="8" direction="row">
        <h2>{texts.widgets.portfolioPerformance.graph.title}</h2>

        <ConnectPopover
          id="portfolio-performance-graph"
          title={texts.widgets.portfolioPerformance.graph.help.title}
          description={
            texts.widgets.portfolioPerformance.graph.help.description
          }
        />
      </WithGap>

      <FilterContainer>
        <DateFilterChips
          id="portfolio-performance"
          checked={selectedPeriod}
          onChange={(period) => {
            handlePeriodChange(period, null, null);
          }}
        />

        <UnitChips
          id="portfolio-performance"
          checked={selectedUnit}
          onChange={setSelectedUnit}
        />
      </FilterContainer>

      {DateFilterType.Custom === selectedPeriod && (
        <div>
          <DateRangeSelector
            initialStartDate={startDate}
            initialEndDate={endDate}
            onChange={(startDate, endDate) => {
              handlePeriodChange(DateFilterType.Custom, startDate, endDate);
            }}
          />
        </div>
      )}

      <FixedHeight
        height={
          /* Graph */ 360 + /* <PortfolioReturnText /> */ 24 + /* Gap */ 24
        }
      >
        {isNotReady(portfolioPerformanceQuery) ? (
          <QueryState query={portfolioPerformanceQuery} />
        ) : (
          <WithGap>
            <PortfolioReturnText
              portfolioPerformance={portfolioPerformanceQuery.data!}
              unit={selectedUnit}
            />

            <PortfolioPerformanceGraph
              height={360}
              unit={selectedUnit}
              trackId={`portfolio-widget-performance-${portfolioGroup}`}
              data={
                portfolioPerformanceQuery.data!.performanceMap[selectedUnit]
              }
            />
          </WithGap>
        )}
      </FixedHeight>
      {showMoreLink && (
        <div>
          <Button
            variant="tertiary"
            as={InternalLink}
            to={`/${organizationCmId}/performance`}
            // Due to Storeblocks Button not having properly implemented the
            // `as` prop, we need to use the `@ts-ignore` directive here to
            // be able to use props from the "as" component.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state={{ portfolioGroup }}
            iconRight="arrow_right"
            data-trackid={getTrackId(
              `go-to-performance-dashboard-${portfolioGroup}`,
              TrackingElement.Link,
            )}
          >
            {texts.widgets.portfolioPerformance.goToPortfolioDashboard}
          </Button>
        </div>
      )}
    </WithGap>
  );
};

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
